import * as React from "react";
import {
  Button,
  Box,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BaseModal from "./global/BaseModal";

interface ConfirmDeleteModalProps {
  open: boolean;
  title?: string;
  message: string;
  onDelete: () => Promise<void>;
  onClose: () => void;
}

const ConfirmDeleteModal = ({
  open,
  title,
  message,
  onClose,
  onDelete,
}: ConfirmDeleteModalProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [loading, setLoading] = React.useState(false);

  const handleDelete = React.useCallback(() => {
    setLoading(true);
    onDelete().finally(() => {
      setLoading(false);
    });
  }, [onDelete]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <LoadingButton
        onClick={handleDelete}
        autoFocus
        color="error"
        loading={loading}
        variant="contained"
      >
        Delete
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title || "Confirm Deletion"}
      footer={footer}
      maxWidth="xs"
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          "@media (orientation: landscape) and (max-width: 900px)": {
            p: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            "@media (orientation: landscape) and (max-width: 900px)": {
              width: 48,
              height: 48,
              mb: 1.5,
            },
            background: isDark
              ? `linear-gradient(135deg, ${alpha("#d32f2f", 0.2)} 0%, ${alpha("#d32f2f", 0.1)} 100%)`
              : `linear-gradient(135deg, ${alpha("#d32f2f", 0.15)} 0%, ${alpha("#d32f2f", 0.08)} 100%)`,
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: `2px solid ${alpha("#d32f2f", 0.3)}`,
          }}
        >
          <WarningAmberIcon
            sx={{
              fontSize: 36,
              color: "#d32f2f",
              "@media (orientation: landscape) and (max-width: 900px)": {
                fontSize: 28,
              },
            }}
          />
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            fontWeight: 500,
            lineHeight: 1.6,
            wordBreak: "break-word",
            "@media (orientation: landscape) and (max-width: 900px)": {
              fontSize: "0.875rem",
              lineHeight: 1.5,
            },
          }}
        >
          {message || ""}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mt: 1.5,
            fontSize: "0.875rem",
            "@media (orientation: landscape) and (max-width: 900px)": {
              fontSize: "0.8125rem",
              mt: 1,
            },
          }}
        >
          This action cannot be undone.
        </Typography>
      </Box>
    </BaseModal>
  );
};
export default ConfirmDeleteModal;
